import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  IconButton,
} from "@mui/material";
import { ModeloGetUrlJson } from "../../ModeloCrud"; // Ajusta la ruta según tu estructura de carpetas
import { toast } from "react-toastify";
import FacebookCircularProgress from "../../FacebookCircularProgress";
import HistoriaClinicaTab from "../tabs/HistoriaClinicaTab";
import CuentaCorrienteTab from "../tabs/CuentaCorrienteTab";
import PresupuestosTab from "../tabs/PresupuestosTab";
import NotasTab from "../tabs/NotasTab"; // Asegúrate de que este componente exista
import OdontogramasTab from "../tabs/OdontogramasTab"; // Asegúrate de que este componente exista
import CloseIcon from "@mui/icons-material/Close";

const AuditoriaPacientePanel = ({ open, onClose, paciente }) => {
  const [activeTab, setActiveTab] = useState(4);
  const [loading, setLoading] = useState(true);
  const [pacienteData, setPacienteData] = useState(null);

  useEffect(() => {
    const anchoPantalla = window.innerWidth;
    console.log("Ancho del viewport:", anchoPantalla);
    if (paciente) {
      // Obtener la fecha actual
      const today = new Date();
      // Restar 5 años
      today.setFullYear(today.getFullYear() - 4);
      // Formatear la fecha en el formato yyyy-mm-dd
      const formattedDate = today.toISOString().split("T")[0];
      // Hacer la solicitud cuando se recibe el paciente
      const params = new URLSearchParams({
        paciente_id: paciente.id,
        fecha: formattedDate, // Fecha formateada
      });
      console.log("Parámetros de la solicitud:", params.toString());
      const fetchPacienteData = async () => {
        try {
          // Suponiendo que tienes una URL para obtener los datos del paciente
          setLoading(true); // Cambiar estado de carga
          const response = await ModeloGetUrlJson(
            "paciente",
            paciente.id,
            "paciente-vista-auditoria",
            params
          );
          setLoading(false); // Dejar de cargar
          if (response.error === false) {
            // Extraemos solo los datos necesarios
            const {
              ctacte,
              fichados,
              fichados_items,
              fotoUrl,
              observaciones,
              presupuestos,
              "presupuestos-lineas": presupuestosLineas,
              tratamientos,
              presupuestos_full,
              presupuestos_full_lineas,
            } = response;

            // Guardamos solo los datos relevantes en el estado
            setPacienteData({
              ctacte: ctacte ?? [],
              fichados: fichados ?? [],
              fichados_items: fichados_items ?? [],
              fotoUrl: fotoUrl ?? "default.jpg",
              observaciones: observaciones ?? [],
              presupuestos: presupuestos ?? [],
              "presupuestos-lineas": presupuestosLineas ?? [],
              tratamientos: tratamientos ?? [],
              presupuestos_full: presupuestos_full ?? [],
              presupuestos_full_lineas: presupuestos_full_lineas ?? [],
            });
            console.log("Datos del paciente:", response);
          } else {
            toast.error(
              "Error al cargar los datos del paciente: " + response.mensaje
            );
          }
          // Actualizar el estado con los datos recibidos
        } catch (error) {
          console.error("Error fetching paciente data:", error);
          setLoading(false); // Dejar de cargar en caso de error
        }
      };

      fetchPacienteData(); // Llamada a la API
    }
  }, [paciente]); // Ejecutar cada vez que el paciente cambie

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Liberar la foto cuando se cierra el panel
  const handleClose = () => {
    onClose(); // Ejecutar cualquier función pasada como prop para cerrar el panel
    setPacienteData(null); // Limpiar los datos del paciente (y la foto)
    setActiveTab(4); // Resetear la pestaña activa
  };

  const sortedData = (pacienteData?.ctacte || []).sort((a, b) => {
    // Primero, ordenar por fecha_usa (fecha) en orden descendente
    const dateA = new Date(a.fecha_usa);
    const dateB = new Date(b.fecha_usa);
    if (dateA < dateB) return 1; // Si 'a' es anterior, se coloca después
    if (dateA > dateB) return -1; // Si 'a' es posterior, se coloca antes

    // Si las fechas son iguales, ordenamos por tipo_prev en orden ascendente
    const idA = a.id || "";
    const idB = b.id || "";
    if (idA < idB) return -1;
    if (idA > idB) return 1;
  });

  const sortedPresupuestos = (pacienteData?.presupuestos_full || [])
    .filter((presupuesto) => presupuesto.estado === "APROBADO") // Filtramos solo los aprobados
    .sort((a, b) => {
      // Primero, ordenar por fecha_emision en orden descendente
      const dateA = new Date(a.fecha_emision);
      const dateB = new Date(b.fecha_emision);
      if (dateA < dateB) return 1; // Si 'a' es anterior, se coloca después
      if (dateA > dateB) return -1; // Si 'a' es posterior, se coloca antes

      // Si las fechas son iguales, ordenamos por id en orden ascendente (opcional, puedes ajustar este paso)
      const idA = a.id || "";
      const idB = b.id || "";
      if (idA < idB) return -1;
      if (idA > idB) return 1;

      return 0; // En caso de que ambos elementos sean iguales
    });

  const filterPresupuestosLineas = (
    pacienteData?.presupuestos_full_lineas || []
  ).filter((linea) => linea.aprobado === 1);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: "80vw" } }}
    >
      <Box p={0}>
        <Card>
          <CardHeader
            avatar={
              <Avatar
                src={
                  loading
                    ? "/img/no-avatar.png"
                    : pacienteData?.fotoUrl || "/img/no-avatar.png"
                }
                alt={paciente?.nombre}
                sx={{ width: 100, height: 100 }}
              />
            }
            title={
              <Typography variant="h6" sx={{ textTransform: "capitalize" }}>
                {paciente?.nombre.toLowerCase()}
              </Typography>
            }
            subheader={`Documento: ${paciente?.documento}`}
            action={
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleClose}
                sx={{ margintTop: 10, marginRight: 10 }}
              >
                <CloseIcon />
              </IconButton>
            }
          />
          {loading && (
            <div className="flex justify-center mt-2">
              <FacebookCircularProgress />{" "}
            </div>
          )}
          <CardContent>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ mt: -4 }}
            >
              <Tab label="Cuenta Corriente" />
              <Tab label="Presupuestos" />
              <Tab label="Historia Clínica" />
              <Tab label="Notas" />
              <Tab label="Odontogramas" />
            </Tabs>

            <Box mt={2}>
              {activeTab === 0 && (
                <CuentaCorrienteTab dataRows={sortedData || []} />
              )}
              {activeTab === 1 && (
                <PresupuestosTab
                  rowsPadre={sortedPresupuestos || []}
                  rowsHijo={filterPresupuestosLineas || []}
                />
              )}
              {activeTab === 2 && (
                <HistoriaClinicaTab
                  dataRows={pacienteData?.tratamientos || []}
                />
              )}
              {activeTab === 3 && (
                <NotasTab dataRows={pacienteData?.observaciones || []} />
              )}
              {activeTab === 4 && (
                <OdontogramasTab
                  rowsPadre={pacienteData?.fichados || []}
                  rowsHijo={pacienteData?.fichados_items || []}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Drawer>
  );
};

export default AuditoriaPacientePanel;
